import React from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../../state/mint/hooks'
import { TokenAmount } from '@uniswap/sdk-core'
import { Field } from '../../../../state/mint/actions'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../../hooks'
import { useWalletModalToggle } from '../../../../state/application/hooks'
import { ButtonConfirmed, ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { RowBetween } from '../../../../components/Row'
import { ApprovalState, useApproveCallback } from '../../../../hooks/useApproveCallback'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { formatUnits, parseUnits } from 'ethers/lib/utils'
import { useTokenBalance } from '../../../../state/wallet/hooks'
import { calculateGasMargin, getContract } from '../../../../utils'
import { Dots } from '../../../Pool/styleds'
import { Text } from 'rebass'
import { STHYPE_OVERSEER_ADDRESS, STHYPE_ADDRESS } from '@hyperswap/addresses'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'
import { useToken } from 'hooks/Tokens'
import { abi as STHypeOverseerABI } from '../../../../abis/sthype_overseer.json'
import { BigNumber } from 'ethers'

interface UnstakeProps {
  key?: number
  setRefresh: () => void
  maxRedeemable: undefined | BigNumber
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const Unstake: React.FC<UnstakeProps> = ({ setRefresh, maxRedeemable }) => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const token = useToken(STHYPE_ADDRESS)
  const tokenBalance = useTokenBalance(account ?? undefined, token ?? undefined)

  // allowance handling
  const [signatureData] = React.useState<{
    v: number
    r: string
    s: string
    deadline: number
  } | null>(null)

  const [loadingRemove, setLoadingRemove] = React.useState<boolean>(false)
  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(token ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], STHYPE_OVERSEER_ADDRESS)

  async function onAttemptToApprove() {
    const liquidityAmount = parsedAmounts[Field.CURRENCY_A]

    if (!liquidityAmount) throw new Error('missing liquidity amount')
    approveCallback()
  }

  const addTransaction = useTransactionAdder()

  const maxRedeemableFormatted = formatUnits(maxRedeemable ? maxRedeemable : BigNumber.from(0), 18)
  const maxRedeemableTooLow = parseFloat(maxRedeemableFormatted) < parseFloat(formattedAmounts[Field.CURRENCY_A])

  async function onWithdraw() {
    if (!chainId || !library || !account) return
    const overseerV1Contract = getContract(STHYPE_OVERSEER_ADDRESS, STHypeOverseerABI, library, account)
    const estimate = overseerV1Contract.estimateGas.burnAndRedeemIfPossible
    const method = overseerV1Contract.burnAndRedeemIfPossible
    const args = [
      account,
      maxRedeemableTooLow
        ? parseUnits(maxRedeemableFormatted, 18).toString()
        : parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18).toString(),
      '',
    ]

    setLoadingRemove(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Unstake HYPE`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh()
          setLoadingRemove(false)
        })
      )
      .catch((error) => {
        setLoadingRemove(false)
        console.error(error)
      })
  }

  const isDisabled =
    loadingRemove === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    (tokenBalance && parseFloat(formattedAmounts[Field.CURRENCY_A]) > parseFloat(tokenBalance?.toExact())) ||
    approval !== ApprovalState.APPROVED

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id="unstake-sthype"
        showCommonBases
        smallTokenName
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
        ) : (
          <RowBetween>
            <ButtonConfirmed
              onClick={onAttemptToApprove}
              confirmed={approval === ApprovalState.APPROVED || signatureData !== null}
              disabled={approval !== ApprovalState.NOT_APPROVED || signatureData !== null}
              mr="0.5rem"
              fontWeight={400}
              fontSize={12}
            >
              {approval === ApprovalState.PENDING ? (
                <Dots>Approving</Dots>
              ) : approval === ApprovalState.APPROVED || signatureData !== null ? (
                'Approved'
              ) : (
                'Approve'
              )}
            </ButtonConfirmed>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onWithdraw()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingRemove ? 'Unstaking...' : 'Unstake'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
        {maxRedeemableTooLow && (
          <p style={{ color: '#FD4040', marginBottom: '0', fontSize: '12px' }}>
            Note: The max redeemable amount is lower than your unstake request. Your request will be batched, and you
            can come back later to claim the rest.
          </p>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Unstake
